import { Head, Link } from "@inertiajs/react"
import {
  Anchor,
  Banknote,
  Binoculars,
  BookOpen,
  CalendarDays,
  ChevronRight,
  CircleDot,
  Coffee,
  Croissant,
  Cross,
  Map as MapIcon,
  MapPin,
  Ship,
  ShoppingBasket,
  SquareParking,
  Stethoscope,
  Tent,
  Ticket,
  Utensils,
  Waves,
  type LucideIcon,
} from "lucide-react"
import { PublicShell } from "@/components/PublicShell"
import { Button } from "@/components/ui/button"
import {
  canonicalHref,
  localePathsFrom,
  publicMetaDescription,
  type AlternateLink,
  type PublicLocale,
} from "@/lib/public-links"

type PublicDestination = {
  id: number
  name: string
  slug: string
  kind: string
  intro: string
  summary: string | null
  meta_title: string | null
  meta_description: string | null
  latitude: number | null
  longitude: number | null
  path: string
}

type CategorySummary = {
  id: number
  label: string
  icon: string
  map_color: string
  place_count: number
  path: string
}

type Photo = {
  id: number
  alt_text: string
  credit: string | null
  primary: boolean
  hero_url: string
}

type GuideSummary = { id: number; name: string; summary: string; category: string; path: string }

const CATEGORY_ICONS: Record<string, LucideIcon> = {
  anchor: Anchor,
  banknote: Banknote,
  binoculars: Binoculars,
  coffee: Coffee,
  croissant: Croissant,
  cross: Cross,
  "parking-square": SquareParking,
  ship: Ship,
  "shopping-basket": ShoppingBasket,
  stethoscope: Stethoscope,
  tent: Tent,
  ticket: Ticket,
  utensils: Utensils,
  waves: Waves,
}

const COPY = {
  en: {
    home: "Home",
    kicker: "Destination field guide",
    titleSuffix: "field guide",
    summaryTitle: "What to know",
    childrenTitle: "Towns and areas",
    childrenIntro: "Choose a local hub for more specific guidance around the island.",
    emptyTitle: "Local hubs are being prepared",
    emptyText: "This destination is available first; town and area pages will appear as editors publish them.",
    mapTitle: "Map preview",
    staticPreview: "Static preview",
    openMap: "Open map",
    explore: "Explore",
    categoriesTitle: "Places by category",
    categoriesIntro: "A live count of published places on this destination and its local hubs.",
    categoriesEmpty: "No place categories have been published here yet.",
    place: "place",
    places: "places",
    guidesTitle: "Featured guides",
    guidesEmpty: "Editorial guides are being prepared. We will show them here only after they are ready to publish.",
    eventsTitle: "Upcoming events",
    eventsText: "Browse confirmed future events in date order. Recurring events without dates remain available on their detail pages.",
    coverageTitle: "What is available now",
    coverageText: "Destination and town guidance is live. Category counts reflect published places; full listings, transport pages, and editorial guides arrive in upcoming releases.",
  },
  de: {
    home: "Startseite",
    kicker: "Reiseziel-Führer",
    titleSuffix: "Reiseführer",
    summaryTitle: "Das Wichtigste",
    childrenTitle: "Orte und Gebiete",
    childrenIntro: "Wähle einen lokalen Ort für genauere Hinweise rund um die Insel.",
    emptyTitle: "Lokale Seiten werden vorbereitet",
    emptyText: "Dieses Reiseziel ist zuerst verfügbar; Orts- und Gebietsseiten erscheinen nach der redaktionellen Freigabe.",
    mapTitle: "Kartenvorschau",
    staticPreview: "Statische Vorschau",
    openMap: "Karte öffnen",
    explore: "Entdecken",
    categoriesTitle: "Orte nach Kategorie",
    categoriesIntro: "Eine aktuelle Anzahl veröffentlichter Orte für dieses Reiseziel und seine lokalen Seiten.",
    categoriesEmpty: "Hier wurden noch keine Ortskategorien veröffentlicht.",
    place: "Ort",
    places: "Orte",
    guidesTitle: "Ausgewählte Reiseführer",
    guidesEmpty: "Redaktionelle Reiseführer werden vorbereitet. Sie erscheinen hier erst nach ihrer Veröffentlichung.",
    eventsTitle: "Kommende Veranstaltungen",
    eventsText: "Bestätigte zukünftige Veranstaltungen nach Datum. Wiederkehrende Veranstaltungen ohne Termin bleiben über ihre Detailseiten erreichbar.",
    coverageTitle: "Was jetzt verfügbar ist",
    coverageText: "Reiseziel- und Ortsinformationen sind verfügbar. Die Kategorien zeigen veröffentlichte Orte; vollständige Listen, Verkehrsseiten und Reiseführer folgen in kommenden Versionen.",
  },
} satisfies Record<PublicLocale, Record<string, string>>

export default function DestinationShow({
  locale,
  destination,
  child_destinations,
  category_summaries,
  guide_summaries,
  guides_path,
  events_path,
  map_path,
  upcoming_event_count,
  photos,
  alternate_links,
}: {
  locale: PublicLocale
  destination: PublicDestination
  child_destinations: PublicDestination[]
  category_summaries: CategorySummary[]
  guide_summaries: GuideSummary[]
  guides_path: string
  events_path: string
  map_path: string
  upcoming_event_count: number
  photos: Photo[]
  alternate_links: AlternateLink[]
}) {
  const copy = COPY[locale]
  const title = destination.meta_title ?? `${destination.name} ${copy.titleSuffix}`
  const description = publicMetaDescription(
    destination.meta_description ?? destination.intro,
    locale === "de"
      ? `Finde veröffentlichte lokale Hinweise, Orte, Kategorien und Karteninformationen für ${destination.name} in Kroatien.`
      : `Find published local guidance, towns, place categories, and map context for ${destination.name}, Croatia.`,
  )
  const canonical = canonicalHref(alternate_links, locale)
  const heroPhoto = photos.find((photo) => photo.primary) ?? photos[0]

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        {canonical && <link rel="canonical" href={canonical} />}
        {alternate_links.map((link) => (
          <link key={link.href_lang} rel="alternate" hrefLang={link.href_lang} href={link.href} />
        ))}
      </Head>

      <PublicShell locale={locale} localePaths={localePathsFrom(alternate_links)}>
        <main>
          <div className="public-container pt-4 md:pt-6">
            <Breadcrumbs locale={locale} destination={destination} homeLabel={copy.home} />
            {heroPhoto && (
              <figure className="relative mt-4 overflow-hidden rounded-2xl border border-hairline-strong">
                <img src={heroPhoto.hero_url} alt={heroPhoto.alt_text} loading="lazy" className="h-[15rem] w-full object-cover md:h-[28rem]" />
                {heroPhoto.credit && <figcaption className="absolute right-3 bottom-3 rounded-full bg-ink-display/75 px-3 py-1 text-xs text-surface">{heroPhoto.credit}</figcaption>}
              </figure>
            )}
          </div>

          <section className="public-container grid gap-6 py-5 md:grid-cols-[1fr_23.75rem] md:items-start md:gap-8 md:py-8">
            <div>
              <p className="public-kicker">{copy.kicker}</p>
              <h1 className="public-hero-title mt-2 md:mt-3">
                {destination.name} <span className="text-ink-soft">{copy.titleSuffix}</span>
              </h1>
              <p className="public-lede mt-3 md:mt-4">{destination.intro}</p>

              {destination.summary && (
                <section className="public-panel mt-6 p-5 md:p-6">
                  <h2 className="public-section-title">{copy.summaryTitle}</h2>
                  <p className="public-card-description mt-2 max-w-[65ch]">{destination.summary}</p>
                </section>
              )}
            </div>

            <DestinationMapPreview destination={destination} copy={copy} mapPath={map_path} />
          </section>

          <section className="public-container pb-8">
            <h2 className="public-section-title">{copy.childrenTitle}</h2>
            <p className="public-card-description mt-1 max-w-[44rem]">{copy.childrenIntro}</p>

            {child_destinations.length > 0 ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {child_destinations.map((child) => (
                  <Link key={child.id} href={child.path} className="public-card group block p-4 no-underline md:p-5">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent-faded text-accent">
                        <MapPin className="h-4 w-4" aria-hidden />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="public-card-title block">{child.name}</span>
                        <span className="mt-0.5 block text-xs text-ink-muted">{formatKind(child.kind)}</span>
                      </span>
                      <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-ink-muted transition-transform group-hover:translate-x-0.5" aria-hidden />
                    </div>
                    {child.summary && <span className="public-card-description mt-3 line-clamp-3 block">{child.summary}</span>}
                    <span className="mt-3 block text-sm font-semibold text-accent">{copy.explore}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="public-muted-card mt-4 p-5">
                <div className="public-card-title text-ink-muted">{copy.emptyTitle}</div>
                <div className="public-card-description mt-1">{copy.emptyText}</div>
              </div>
            )}
          </section>

          <section className="public-container pb-8">
            <h2 className="public-section-title">{copy.categoriesTitle}</h2>
            <p className="public-card-description mt-1 max-w-[44rem]">{copy.categoriesIntro}</p>

            {category_summaries.length > 0 ? (
              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {category_summaries.map((category) => (
                  <CategorySummaryCard key={category.id} category={category} copy={copy} />
                ))}
              </div>
            ) : (
              <div className="public-muted-card mt-4 p-5">
                <div className="public-card-description">{copy.categoriesEmpty}</div>
              </div>
            )}
          </section>

          <section className="public-container pb-8">
            <div className="flex items-center justify-between gap-4"><h2 className="public-section-title">{copy.guidesTitle}</h2>{guide_summaries.length > 0 && <Link href={guides_path} className="text-sm font-semibold">{copy.explore}</Link>}</div>
            {guide_summaries.length > 0 ? <div className="mt-4 grid gap-3 md:grid-cols-2">{guide_summaries.map((guide) => <Link key={guide.id} href={guide.path} className="public-card flex gap-3 p-5 no-underline"><BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden /><span><span className="public-kicker capitalize">{guide.category}</span><strong className="public-card-title mt-1 block">{guide.name}</strong><span className="public-card-description mt-2 block">{guide.summary}</span></span></Link>)}</div> : <div className="public-muted-card mt-4 flex items-start gap-3 p-5"><BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-ink-muted" aria-hidden /><p className="public-card-description">{copy.guidesEmpty}</p></div>}
          </section>

          <section className="public-container pb-8"><h2 className="public-section-title">{copy.eventsTitle}</h2><Link href={events_path} className="public-card mt-4 flex items-center gap-4 p-5 no-underline"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-accent-faded text-accent"><CalendarDays className="h-5 w-5" /></span><span className="flex-1"><strong className="public-card-title block">{copy.eventsTitle}</strong><span className="public-card-description mt-1 block">{copy.eventsText}</span></span><span className="rounded-full bg-accent px-3 py-1 text-sm font-bold text-surface">{upcoming_event_count}</span></Link></section>

          <section className="public-container pb-9">
            <div className="rounded-2xl bg-surface-subtle p-5 md:flex md:items-baseline md:justify-between md:gap-8 md:p-6">
              <h2 className="public-section-title shrink-0">{copy.coverageTitle}</h2>
              <p className="public-card-description mt-2 max-w-[65ch] md:mt-0">{copy.coverageText}</p>
            </div>
          </section>
        </main>
      </PublicShell>
    </>
  )
}

function CategorySummaryCard({ category, copy }: { category: CategorySummary; copy: (typeof COPY)[PublicLocale] }) {
  const Icon = CATEGORY_ICONS[category.icon] ?? CircleDot

  return (
    <Link href={category.path} className="public-card flex items-center gap-3 p-4 no-underline md:p-5">
      <span
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
        style={{ backgroundColor: category.map_color }}
      >
        <Icon className="h-5 w-5" aria-hidden />
      </span>
      <span>
        <span className="public-card-title block">{category.label}</span>
        <span className="mt-0.5 block text-sm text-ink-muted">
          {category.place_count} {category.place_count === 1 ? copy.place : copy.places}
        </span>
      </span>
    </Link>
  )
}

function Breadcrumbs({
  locale,
  destination,
  homeLabel,
}: {
  locale: PublicLocale
  destination: PublicDestination
  homeLabel: string
}) {
  return (
    <nav className="flex flex-wrap items-center gap-1.5 text-[0.78rem] text-ink-muted" aria-label="Breadcrumb">
      <Link href={`/${locale}`} className="text-ink-muted no-underline hover:text-ink-display">{homeLabel}</Link>
      <ChevronRight className="h-3 w-3" aria-hidden />
      <span className="font-semibold text-ink-secondary">{destination.name}</span>
    </nav>
  )
}

function DestinationMapPreview({ destination, copy, mapPath }: { destination: PublicDestination; copy: (typeof COPY)[PublicLocale]; mapPath: string }) {
  return (
    <section className="public-panel overflow-hidden">
      <div className="p-5 pb-3">
        <h2 className="public-card-title flex items-center gap-2">
          <MapIcon className="h-4 w-4 text-accent" aria-hidden />
          {copy.mapTitle}
        </h2>
      </div>
      <div className="relative h-[14rem] overflow-hidden bg-accent-faded">
        <div className="absolute -left-16 -top-16 h-52 w-72 rounded-[46%_54%_62%_38%/55%_48%_52%_45%] bg-surface-tinted" />
        <div className="absolute -right-12 -bottom-16 h-44 w-60 rounded-[56%_44%_42%_58%/45%_58%_42%_55%] bg-surface-tinted" />
        <div className="absolute left-8 top-8 font-mono text-[0.63rem] font-semibold uppercase tracking-[0.12em] text-ink-muted">{destination.name}</div>
        <MapPin className="absolute left-1/2 top-1/2 h-10 w-10 -translate-x-1/2 -translate-y-full fill-accent-hover text-accent-hover drop-shadow" aria-hidden />
        <span className="absolute bottom-3 left-3 rounded-full bg-surface/90 px-2.5 py-1 text-[0.68rem] font-semibold text-ink-muted">{copy.staticPreview}</span>
        <Button asChild size="sm" className="absolute right-3 bottom-3 rounded-full">
          <Link href={mapPath}>
            <MapIcon className="h-3.5 w-3.5" aria-hidden />
            {copy.openMap}
          </Link>
        </Button>
      </div>
    </section>
  )
}

function formatKind(kind: string) {
  return kind.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ")
}
